import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { employeesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Loader2, Clock, Calendar, DollarSign, Receipt, FileText, UserCircle } from 'lucide-react';
import { useAuth } from '../App';

const MyDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState({ employee: null, payslip: null, leaveBalance: [], pendingLeaves: 0, pendingExpenses: 0, today: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [empRes, payslipsRes, balRes, leavesRes, expensesRes, attRes] = await Promise.all([
          api.get('/me/employee'),
          api.get('/me/payslips'),
          api.get('/me/leave-balance'),
          api.get('/me/leaves'),
          api.get('/me/expenses'),
          api.get('/me/attendance'),
        ]);
        const today = new Date().toISOString().split('T')[0];
        setData({
          employee: empRes.data,
          payslip: payslipsRes.data[0] || null,
          leaveBalance: balRes.data || [],
          pendingLeaves: leavesRes.data.filter(l => l.status === 'pending').length,
          pendingExpenses: expensesRes.data.filter(e => e.status === 'pending').length,
          today: attRes.data.find(a => a.date === today) || null,
        });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const emp = data.employee;
  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <div>
        <h1 className="text-2xl font-bold">Welcome back, {emp?.first_name}!</h1>
        <p className="text-muted-foreground">{emp?.designation} · {emp?.department}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card data-testid="card-attendance-today">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Clock size={16} className="text-primary" /><span className="text-xs text-muted-foreground">Today</span></div>
            {data.today ? (
              <>
                <p className="text-sm font-bold">{data.today.punch_in ? new Date(data.today.punch_in).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '—'}</p>
                <p className="text-xs text-muted-foreground">{data.today.punch_out ? 'Punched out' : 'Active'}</p>
              </>
            ) : <p className="text-xs">Not punched in</p>}
          </CardContent>
        </Card>
        <Card data-testid="card-leave-balance">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Calendar size={16} className="text-emerald-500" /><span className="text-xs text-muted-foreground">Leave Balance</span></div>
            <p className="text-2xl font-bold">{data.leaveBalance.reduce((s, b) => s + (b.remaining || 0), 0)}</p>
            <p className="text-xs text-muted-foreground">days available</p>
          </CardContent>
        </Card>
        <Card data-testid="card-pending-leaves">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Calendar size={16} className="text-amber-500" /><span className="text-xs text-muted-foreground">Pending Leaves</span></div>
            <p className="text-2xl font-bold">{data.pendingLeaves}</p>
          </CardContent>
        </Card>
        <Card data-testid="card-pending-expenses">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Receipt size={16} className="text-purple-500" /><span className="text-xs text-muted-foreground">Pending Expenses</span></div>
            <p className="text-2xl font-bold">{data.pendingExpenses}</p>
          </CardContent>
        </Card>
      </div>

      {data.payslip && (
        <Card>
          <CardHeader><CardTitle className="text-base">Latest Payslip</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div><p className="text-xs text-muted-foreground">Month</p><p className="font-bold">{data.payslip.month}/{data.payslip.year}</p></div>
              <div><p className="text-xs text-muted-foreground">Gross</p><p className="font-bold">{data.payslip.currency} {data.payslip.gross_salary?.toLocaleString()}</p></div>
              <div><p className="text-xs text-muted-foreground">Deductions</p><p className="font-bold text-red-500">{data.payslip.currency} {data.payslip.total_deductions?.toLocaleString()}</p></div>
              <div><p className="text-xs text-muted-foreground">Net</p><p className="font-bold text-emerald-500">{data.payslip.currency} {data.payslip.net_salary?.toLocaleString()}</p></div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { to: '/me/attendance', icon: Clock, label: 'My Attendance' },
          { to: '/me/leave', icon: Calendar, label: 'My Leave' },
          { to: '/me/payroll', icon: DollarSign, label: 'My Payroll' },
          { to: '/me/expenses', icon: Receipt, label: 'My Expenses' },
          { to: '/me/documents', icon: FileText, label: 'My Documents' },
          { to: '/me/profile', icon: UserCircle, label: 'My Profile' },
        ].map(item => (
          <Link key={item.to} to={item.to} data-testid={`portal-link-${item.label.toLowerCase().replace(/\s/g,'-')}`}>
            <Card className="card-hover h-full cursor-pointer">
              <CardContent className="p-4 flex flex-col items-center gap-2">
                <item.icon size={24} className="text-primary" />
                <span className="text-sm font-medium">{item.label}</span>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default MyDashboard;
